import getConfig from 'next/config';

import {
  setModuleLocaleData, setMetaData, setProducts, setHeaderConfig
} from '../actions/load';
import makeApiRequest from '../utils/makeApiRequest';
import makeOrCacheApiReq from '../utils/apiCache';
import { readCookie } from '../utils/cookies-browser';
import { getModuleLocaleDataKey } from '../utils/i18n';
import redirectToMobileSite, { checkMobile } from '../utils/redirectToMobileSite.mjs';
import { getHostName } from '../utils/urlUtils';
import metaInfo from '../config/metaInfo';
import { gwGet } from '../actions/actionsFactory/gwRequest';
import { getProducts } from '../actions/actionsSet/assetService';
import { setPublicAssetsStatus, hideHeaderLangSelect } from '../actions/actionsSet/userConfig';
import {
  wssUri,
} from './handleInitTask';
import bog from '../utils/moreBog';
import {useConfig} from '@binance/hooks'

const {
  publicRuntimeConfig: {
    siteUrl: WEB_SITE_URL,
    routePrefix: ROUTE_PREFIX,
    assetPrefix,
    site: SITE,
  },
} = getConfig();

const {
  brands, metaTitles, metaKeywords, metaDescriptions,
} = metaInfo;

// modules which will be split
const I18N_API_MODULES = ['blog', 'exchange-seo'];
const isUsingModuleI18n = module => !!module && I18N_API_MODULES.includes(module);

// node apis served in server.js
const I18N_API_PATH = (module, locale) => isUsingModuleI18n(module) ?
  `${ROUTE_PREFIX}/en/i18n/${module}/${locale}.json` :
  `${ROUTE_PREFIX}/en/i18n/${locale}.json`;

// cache key
const CACHE_API_REQ_KEY = (module, locale) => isUsingModuleI18n(module) ?
  `beta-i18n-${module}-${locale}` :
  `beta-i18n-${locale}`;

export type Context = {
  isServer: boolean,
  req: any,
  query: any,
  store: Object,
  res: any,
};

export const getHeaderConfig = async (store, req, locale) => {
  const ss = store.getState()
  let configs = {}
  if (!ss || ss.baseConfigs) return

  const origin = `https://${getHostName(req)}/`
  const STATIC_HOST = assetPrefix ? assetPrefix : 'https://static.devfdg.net'
  try {
    configs = await useConfig({ staticUrl: STATIC_HOST, origin, locale })
  } catch (error) {
    bog.error('\n\n\n\n\nrequest header config error: ', error)
  }
  store.dispatch(setHeaderConfig(configs))
}

// !!! only ONE `await` allowed in this code !!!
export default async (
  {
    isServer, req, query, store, res,
  }: Context,
  {
    withProductsData, realRedirect, module,
  } = {
    withProductsData: false,
    realRedirect: true,
    module: '',
  }
) => {
  // client side only mobile redirect
  if (realRedirect && !isServer) redirectToMobileSite(isServer, req, res, '/m-HomePage.html');
  if (!query) query = {};

  const shouldPublicAssets = isServer ? req.cookies.shouldPublicAssets : readCookie('shouldPublicAssets');
  store.dispatch(setPublicAssetsStatus(shouldPublicAssets === 'true'));

  const isHideHeadLangSeclet = isServer ? req.cookies.isHideHeadLangSeclet : readCookie('isHideHeadLangSeclet');
  store.dispatch(hideHeaderLangSelect(isHideHeadLangSeclet === 'true'));

  // only ONE `await` allowed in this code
  const promises = []; // do requests ASYNC
  const locale = query.locale || 'en';
  const ogImage = query.snapshot;
  const localeDataKey = getModuleLocaleDataKey(module, locale);

  if (withProductsData) {
    // 1
    promises.push(getProducts(req));
  }

  let localeData = store.getState().localeData[localeDataKey];

  if (!localeData) {
    // 3
    promises.push(
      makeOrCacheApiReq(
        CACHE_API_REQ_KEY(module, locale),
        () => makeApiRequest(I18N_API_PATH(module, locale), req, null, null, true)
      )
    );
  }

  // FETCH in parallel
  const [...responses] = await Promise.all(promises);

  if (withProductsData) {
    // 1
    const products = responses.shift();
    store.dispatch(setProducts(products));
  }

  // 3
  if (!localeData) {
    localeData = responses.shift();
  }
  store.dispatch(setModuleLocaleData(module, locale, localeData));

  const metaData = {
    title: (metaTitles[locale] || metaTitles.en) || `${localeData.Binance || brands[locale] || 'Binance'}`,
    keywords: (metaKeywords[locale] || metaKeywords.en) || '',
    description: (metaDescriptions[locale] || metaDescriptions.en) || '',
    ogImage,
  };
  store.dispatch(setMetaData(metaData));

  await getHeaderConfig(store, req, locale)

  // JUST data for this page, not shared
  return {
    metaData,
    locale,
    wssUri: wssUri(req),
    hostName: getHostName(req),
    isMobile: checkMobile(isServer, req),
    hostUrl: WEB_SITE_URL,
  };
};
